import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AppStore } from '@app/app.store';
import { FieldOption } from '@app/shared/models/field.interface';
import { TranslationValue } from '@app/shared/models/translation/translation-value.interface.';
import { Translation } from '@app/shared/models/translation/translation.interface';
import { TranslateService } from '@ngx-translate/core';
import { v4 as uuid } from 'uuid';
import { DialogBaseComponent } from '../dialog-base/dialog-base.component';

export interface TranslateDialogData {
  dialogTitle: string;
  descriptionText: string;
  translation: Translation;
  readOnlyMode: boolean;
}

export interface TranslationDialogResult {
  accepted: boolean;
  currentLocaleValue: string;
}

interface TranslationFormItem {
  id: string;
  control: FormControl;
}

interface TranslationFormGroup {
  select: TranslationFormItem;
  input: TranslationFormItem;
}

@Component({
  selector: 'vfs-translate-dialog',
  templateUrl: './translate-dialog.component.html',
  styleUrls: ['./translate-dialog.component.scss']
})
export class TranslateDialogComponent extends DialogBaseComponent implements OnInit {
  supportedLocales = ['en', 'es', 'it', 'fr', 'pt'];
  translationGroups: TranslationFormGroup[] = [];
  localeOptions: FieldOption[] = [];
  nameInputControl: FormControl;
  formGroup: FormGroup;
  translation: Translation;
  descriptionText: string;
  readOnlyMode: boolean;

  loginLocale: string;
  langsToExistingValues: Map<string, TranslationValue> = new Map();

  constructor(
    dialogRef: MatDialogRef<any>,
    @Inject(MAT_DIALOG_DATA) public data: TranslateDialogData,
    protected appStore: AppStore,
    protected translateService: TranslateService,
    private formBuilder: FormBuilder
  ) {
    super(dialogRef);
    this.translation = this.data.translation;
    this.descriptionText = this.data.descriptionText;
    this.readOnlyMode = this.data.readOnlyMode;
  }

  ngOnInit() {
    this.loginLocale = this.appStore.state.login.language;
    this.initLocaleLanguage();
    this.initFormGroup();
    this.setTranslationValuesIntoDialog();
  }

  private initLocaleLanguage(): void {
    this.supportedLocales.forEach((locale: string) => {
      if (locale !== this.loginLocale) {
        this.localeOptions.push({
          label: this.translateService.instant('language.' + locale),
          value: locale
        });
      }
    });
  }

  private initFormGroup(): void {
    this.nameInputControl = this.formBuilder.control({ value: '', disabled: this.readOnlyMode }, [
      Validators.required,
      this.noWhitespaceValidator
    ]);
    this.formGroup = this.formBuilder.group({
      nameInputControl: [{ value: this.nameInputControl, disabled: this.readOnlyMode }]
    });
  }

  private setTranslationValuesIntoDialog(): void {
    this.translation.translationValues.forEach((translationValue: TranslationValue) => {
      if (translationValue.lang === this.loginLocale) {
        this.nameInputControl.setValue(translationValue.value);
      } else {
        const formGroup: TranslationFormGroup = this.addTranslation();
        formGroup.select.control.setValue(translationValue.lang);
        formGroup.input.control.setValue(translationValue.value);
      }

      this.langsToExistingValues.set(translationValue.lang, translationValue);
    });
  }

  addTranslation(): TranslationFormGroup {
    const selectItem: TranslationFormItem = {
      id: uuid(),
      control: this.formBuilder.control({ value: '', disabled: this.readOnlyMode }, [
        Validators.required,
        this.noWhitespaceValidator
      ])
    };
    const inputItem: TranslationFormItem = {
      id: uuid(),
      control: this.formBuilder.control({ value: '', disabled: this.readOnlyMode }, [
        Validators.required,
        this.noWhitespaceValidator
      ])
    };
    const translationGroup: TranslationFormGroup = {
      select: selectItem,
      input: inputItem
    };

    this.translationGroups.push(translationGroup);
    this.formGroup.addControl(selectItem.id, selectItem.control);

    this.formGroup.get(selectItem.id).setValue(this.localeOptions[0].value);
    this.formGroup.addControl(inputItem.id, inputItem.control);

    this.translationGroups.forEach((translationGroup: TranslationFormGroup) =>
      this.checkUniqueValue(translationGroup.select.control)
    );
    return translationGroup;
  }

  deleteTranslation(group: TranslationFormGroup): void {
    const index: number = this.translationGroups.indexOf(group);
    if (index === -1) {
      return;
    }

    this.translationGroups.splice(index, 1);

    this.formGroup.removeControl(group.select.id);
    this.formGroup.removeControl(group.input.id);

    this.translationGroups.forEach((translationGroup: TranslationFormGroup) =>
      this.checkUniqueValue(translationGroup.select.control)
    );
  }

  cannotAddTranslation(): boolean {
    return this.translationGroups.length >= this.supportedLocales.length - 1;
  }

  onSelectChange(changedControl: FormControl): void {
    this.checkUniqueValue(changedControl);

    this.translationGroups
      .map((translationGroup: TranslationFormGroup) => translationGroup.select.control)
      .filter((control: FormControl) => control !== changedControl)
      .forEach((control: FormControl) => this.checkUniqueValue(control));
  }

  private checkUniqueValue(control: FormControl): void {
    control.markAsTouched();
    control.setErrors(null);
    for (let otherControl of Object.values(this.formGroup.controls)) {
      if (otherControl !== control && otherControl.value === control.value) {
        control.setErrors({ notUnique: true });
      }
    }
  }

  onAccept(): void {
    // Clear previous translations
    this.translation.translationValues.splice(0, this.translation.translationValues.length);

    // Create / Update for login locale
    this.createOrUpdate(this.loginLocale, this.nameInputControl.value);

    // Create / Update for others locales
    Array.from(this.translationGroups.values()).forEach((translationGroup: TranslationFormGroup) => {
      this.createOrUpdate(translationGroup.select.control.value, translationGroup.input.control.value);
    });

    this.dialogRef.close({
      accepted: true,
      currentLocaleValue: this.nameInputControl.value.trim()
    });
  }

  private createOrUpdate(locale: string, value: string) {
    let translationValue: TranslationValue = this.langsToExistingValues.get(locale);
    const trimmedValue = value ? value.trim() : null;
    if (translationValue) {
      translationValue.value = trimmedValue;
    } else {
      translationValue = {
        id: null,
        lang: locale,
        value: trimmedValue
      };
    }

    this.translation.translationValues.push(translationValue);
  }

  onCancel() {
    this.dialogRef.close({
      accepted: false
    });
  }

  noWhitespaceValidator(control: FormControl) {
    const isWhitespace = (control.value || '').trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? null : { whitespace: true };
  }

  isAcceptDisabled() {
    return this.readOnlyMode || this.nameInputControl.invalid || this.formGroup.invalid;
  }
}
